import { IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';

import { IconSettings } from 'src/assets/icons/IconSettings';
import { ButtonText } from 'src/components/ButtonText';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { Score, SCORE_SIZE } from 'src/components/Score';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { getUserGoalLabel } from 'src/data/benchmark';
import { alignChannelFromBackend, getChannelPerformanceLabel } from 'src/data/channel';
import { getResourceDocumentLabel } from 'src/data/performance';

import { useStyles } from './useStyles';

const TooltipContent = ({ name, description }) => {
  const styles = useStyles();

  if (!name || !description) {
    return;
  }

  return (
    <div className={styles.tooltipContent}>
      <CustomTypography
        className={styles.tooltipHeader}
        variant={TYPOGRAPHY_VARIANT.semiBold14}
      >
        {name}
      </CustomTypography>
      <CustomTypography variant={TYPOGRAPHY_VARIANT.paragraph12}>{description}</CustomTypography>
    </div>
  );
};

TooltipContent.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
};

TooltipContent.defaultProps = {
  name: null,
  description: null,
};

const getChannelLabel = (channel) =>
  getChannelPerformanceLabel(alignChannelFromBackend(channel)) || getResourceDocumentLabel(channel);

export const ConnectedBenchmark = ({ onSettingsClick, benchmarkConfig, generationTool }) => {
  const styles = useStyles();

  const generationToolIcon = generationTool && <GenerationToolIcon tool={generationTool} />;

  const dataSourceText = `${benchmarkConfig.account?.name} (${getChannelLabel(
    benchmarkConfig.account?.channel
  )})`;
  const benchmarkByText = getUserGoalLabel(benchmarkConfig.comparisonSet?.userGoal);

  return (
    <div className={styles.root}>
      <Score
        score={benchmarkConfig.customScore}
        size={SCORE_SIZE.xxxl}
        generationToolIcon={generationToolIcon}
        alignXxxlSizeIcon
      />

      <div className={styles.content}>
        <div className={styles.header}>
          <CustomTypography variant={TYPOGRAPHY_VARIANT.regular14}>
            Better than {benchmarkConfig.customScore}% of{' '}
            {benchmarkConfig.comparisonSet?.examples?.length} comparable texts in{' '}
            <CustomTooltip
              classes={{ tooltip: styles.tooltip }}
              title={
                <TooltipContent
                  name={benchmarkConfig.comparisonSet?.name}
                  description={benchmarkConfig.comparisonSet?.description}
                />
              }
              arrow
            >
              <ButtonText
                onClick={onSettingsClick}
                className={styles.comparisonSetButton}
              >
                <CustomTypography
                  onClick={onSettingsClick}
                  className={styles.truncatedText}
                  variant={TYPOGRAPHY_VARIANT.regular14}
                >
                  {benchmarkConfig.comparisonSet?.name}
                </CustomTypography>
              </ButtonText>
            </CustomTooltip>
          </CustomTypography>

          <IconButton
            onClick={onSettingsClick}
            className={styles.settingsButton}
          >
            <IconSettings className={styles.settingsIcon} />
          </IconButton>
        </div>

        <div className={styles.footer}>
          <TruncatedTypography
            className={styles.dataSource}
            title={dataSourceText}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            Data source: {dataSourceText}
          </TruncatedTypography>
          <span className={styles.bullet}>∙</span>
          <TruncatedTypography
            className={styles.userGoal}
            title={benchmarkByText}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            Metric: {benchmarkByText}
          </TruncatedTypography>
        </div>
      </div>
    </div>
  );
};

ConnectedBenchmark.propTypes = {
  onSettingsClick: PropTypes.func.isRequired,
  benchmarkConfig: PropTypes.shape({
    account: PropTypes.shape({
      name: PropTypes.string,
      channel: PropTypes.string,
    }),
    comparisonSet: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      examples: PropTypes.array,
      userGoal: PropTypes.string,
    }),
    customScore: PropTypes.number,
    shouldUpgrade: PropTypes.bool,
  }).isRequired,
  generationTool: PropTypes.string.isRequired,
};
